import { ISectionLogger } from "@multiweb/logging";
import { Triplet, Tuple } from "Global/Base/DataTypes/NTuples";
import { IRightsEvaluator, IRightsEvaluatorFactory } from "Global/Base/Rights/RightsEvaluator/types";
import { StandardOperationRights } from "Global/Base/Rights/StandardOperationRights";
import { ArrayUtils } from "Global/Base/Utils/ArrayUtils";
import { IControlWithXGAction } from "UI/CommonUIInterfaces";
import { IBaseControlRights, IEnabledVisualControl } from "UI/UI.BaseControls/ControlInterfaces";
import { IAction } from "UI/UI.Controls/Actions/IAction";
import { IMultiEditObject } from "UI/UI.Controls/DataSource/MultiEdit/MEOWObject";
import { IAsyncTree, IBaseAsyncTreeChildrenResult, IBaseAsyncTreeVisitorResult, ITraverseProcessDetails } from "../../../Global/Base/DataTypes/IAsyncTree";
import { TransparentStack } from "../../../Global/Base/DataTypes/TransparentStack";
import { DIContainer } from "../../../Global/Base/DIContainer";
import { Event } from "../../../Global/Base/Events";
import { InvalidOperationException } from "../../../Global/Base/Exceptions";
import { LogicalOperations, LogicalOperator } from "../../../Global/Base/LogicalOperations";
import { OutputParameter } from "../../../Global/Base/OutputParameter";
import { CancelError, CancellationToken } from "../../../Global/Base/Promises/CancellationTokenSource";
import { IClassItemRights } from "../../../Global/Base/Rights/IClassItemRights";
import {
    IComponentTree,
    IComponentTreeVisitorResult,
    IComponentTreeVisitorVisibleResult,
} from "../../../Global/Base/Rights/IComponentTree";
import { IComponentWithCustomEvaluationContext, IRightsContext } from "../../../Global/Base/Rights/IRightsContext";
import { IRightsContexts } from "../../../Global/Base/Rights/IRightsContexts";
import { IRightsEvaluationScheduler } from "../../../Global/Base/Rights/IRightsEvaluationScheduler";
import { IHasRights, IItemRights, IItemRightsCollection, ItemRightsForRightLevels, RightLevel, RightType } from "../../../Global/Base/Rights/RightTypes";
import { ObjectUtils } from "../../../Global/Base/Utils/ObjectUtils";
import { OWManager } from "../../../OW.Remoting/Remoting/OWManager";
import { RequestContext } from "../../../OW.Remoting/Remoting/RequestContext";
import { ClassItemType, IClassItem, IEvaluatedConditionResult } from "../../../OW.Remoting/Remoting/Rights";
import { ComponentTree, ComponentType } from "../LogicalTree/ComponentTree";
import { ILogicalComponent } from "../LogicalTree/ILogicalComponent";
import { ILogicalContainer } from "../LogicalTree/ILogicalContainer";
import { IEditableControl, IGenericControl, IRunWithoutEdit } from "../UI/Controls/ControlInterfaces";
import { DetailFormAction } from "../UI/XGControls/Actions/DetailFormAction";
import { ReportAction } from "../UI/XGControls/Actions/ReportAction";
import { StandardAction } from "../UI/XGControls/Actions/StandardAction";
import { XGAction } from "../UI/XGControls/Actions/XGAction";
import { IMasterableFrame } from "../UI/XGControls/Frames/FrameInterfaces";
import { IDetailFrame } from "../UI/XGControls/Frames/IDetailMasterableFrame";
import { IEditableFrame } from "../UI/XGControls/Frames/IEditableFrame";
import { IForm } from "../UI/XGControls/IForm";
import { IFrameWithRights } from "../UI/XGControls/IFrame";
import { convertContextFromFrameToRequest } from "../Utilities/SharedUIMethods";
import { AdditionalRightResultInfoType, BasicRightResults, IBasicRightResults } from "./BasicRightsResults";
import { ComponentRightsExtractor } from "./ComponentRightsExtractor";
import { FrameRightsEvaluatorType, RightsEvaluationOverridesType, RightsManagerType, RightTypeEvaluationFunctionType } from "./Definitions";
import { IFormRight } from "./FormRight";

/**
 * Definice práva k vyhodnocení spolu s tokenem umožňujícím vyhodnocení zrušit
 */
export interface ICancellableControlRightDef {
    /** Typ práva k vyhodnocení */
    type: RightType;
    /** Token umožňující zrušit vyhodnocení */
    ct: CancellationToken;
}

export class RightsManager implements RightsManagerType {
    private _PRINT_CONSOLE_DEBUG_LEVEL: number = 0; // TODO-rites: odstranit nebo aspoň vypnout

    protected readonly _frame: IFrameWithRights;

    private _componentTreeGetter: () => IComponentTree<ILogicalComponent, ComponentType> | null;

    private readonly _rightsEvaluatorFactory: IRightsEvaluatorFactory;
    private readonly _rightsEvaluationOverrides: RightsEvaluationOverridesType;
    private __rightTypesToItemRightsPropsMap: Map<RightType, keyof IClassItemRights>;
    protected _scheduler: IRightsEvaluationScheduler;
    private _disposed?: boolean;
    private _runningEvaluationsCount: number = 0;
    private _activeRoleIds: string[];
    private readonly _logger: ISectionLogger;

    public readonly rightsEvaluating: Event<RightsManager, IRightsContext> = new Event<RightsManager, IRightsContext>();
    public readonly rightsEvaluated: Event<RightsManager, IRightsContext> = new Event<RightsManager, IRightsContext>();
    public get runningEvaluationsCount(): number {
        return this._runningEvaluationsCount;
    }
    public get initialized(): boolean {
        return this._componentTree != null;
    }

    private get _componentTree(): IComponentTree<ILogicalComponent, ComponentType> | null {
        return this._componentTreeGetter ? this._componentTreeGetter() : null;
    }

    private get _id(): string {
        return this._frame && this._frame.frameName ? this._frame.frameName : "unknown frame";
    }

    private get _ownerForm(): IForm {
        return this._frame && this._frame.ownerForm;
    }

    private get _ownerFormRights(): Array<IFormRight> | null {
        return this._frame?.ownerForm?.formRights;
    }

    /** Vrací evaluator podle contextu.
     * Způsob vyhodnocení práv se může lišit, jestli se vyhodnocují pro hromadný výběr nebo ne.
     */
    private _getRightsEvaluator(context: IRightsContext): IRightsEvaluator {
        return this._rightsEvaluatorFactory.getContextEvaluator(context);
    }

    constructor(frame: IFrameWithRights, rightsEvaluationOverrides: RightsEvaluationOverridesType,
        @DIContainer.instance.bindType(IRightsEvaluatorFactory) rightsEvaluatorFactory: IRightsEvaluatorFactory,
        @DIContainer.instance.bindFactory(IRightsEvaluationScheduler) schedulerFactory: (id: string, logger: ISectionLogger) => IRightsEvaluationScheduler,
        logger: ISectionLogger,
    ) {
        this._logger = logger;
        this._frame = frame;
        this._rightsEvaluationOverrides = rightsEvaluationOverrides;
        this._rightsEvaluatorFactory = rightsEvaluatorFactory;
        this._scheduler = schedulerFactory(this._id, this._logger);
    }

    /**
     * Vrací vyhodnocovač práv framu této implementace IRightsManageru
     */
    public getFrameRightsEvaluator(): FrameRightsEvaluatorType {
        return (controlWrapper: ILogicalComponent, rightDefinitions: ICancellableControlRightDef[], contexts: IRightsContexts | null, evaluationProcessDetails: IGlobalEvaluationProcessDetails, _ct: CancellationToken) => {
            return this._frameRightsEvaluator(controlWrapper, rightDefinitions, contexts, evaluationProcessDetails);
        };
    }

    private async _getActiveRoleIds(): Promise<string[]> {
        if (!this._activeRoleIds) {
            this._activeRoleIds = await OWManager.getUserRoleIDs();
        }

        return this._activeRoleIds;
    }

    protected static _findNodeDFS<TI, TT extends IAsyncTree<TI>>(node: TT, predicate: (searchedNode: TT | null) => boolean): TT | null {
        if (predicate(node))
            return node;
        for (const c of node.children) {
            const found = RightsManager._findNodeDFS<TI, TT>(c, predicate);
            if (found)
                return found;
        }
        return null;
    }

    public findRightsCollectionForControl(predicate: (node: ComponentTree) => boolean): { evaluatedRights: IHasRights; rights: IItemRightsCollection } | undefined {
        const result = RightsManager._findNodeDFS(this._frame.componentTree, predicate);
        if (!result)
            return undefined;

        const { enabled, postponedRequired, recommended, required, visible, closeForm, validateIf, confirmIf, useDigitalSign, useEncryption, useDecryption, openAsEnabled, showIfDefault, isEditing, interactive, enabledVisual } = result.element.control as IHasRights;
        //vytvoří kopii vyhodnocených práv
        const evaluatedRights: IHasRights = {
            enabled,
            postponedRequired,
            recommended,
            required,
            visible,
            closeForm,
            validateIf,
            confirmIf,
            useDigitalSign,
            useEncryption,
            useDecryption,
            openAsEnabled,
            showIfDefault,
            isEditing,
            interactive,
            enabledVisual,
        };

        return { evaluatedRights, rights: result.componentRightsCollection };
    }
    public setComponentTree(componentTreeGetter: () => IComponentTree<ILogicalComponent, ComponentType>): void {
        this._componentTreeGetter = componentTreeGetter;
    }

    /**
     * Vyhodnotí vybraná práva IED(Insert, Edit, Delete), nastaví výsledky navázanému framu a výsledek vrátí
     * @param rightTypes
     * @param usesFormRights
     * @param cancellationToken
     */
    private async _evaluateAndApplyIEDRights(rightTypes: RightType[], context: IRightsContext | null, usesFormRights?: boolean, cancellationToken?: CancellationToken): Promise<StandardOperationRights | null> {
        let evaluateInsert: boolean = false;
        let evaluateEdit: boolean = false;
        let evaluateDelete: boolean = false;

        let combinedRightType: RightType;

        for (const rightType of rightTypes) {
            switch (rightType) {
                case RightType.insert:
                    evaluateInsert = true;
                    break;
                case RightType.edit:
                    evaluateEdit = true;
                    break;
                case RightType.delete:
                    evaluateDelete = true;
                    break;
                default:
                    continue;
            }

            combinedRightType = combinedRightType | rightType;
        }

        if (evaluateInsert || evaluateEdit || evaluateDelete) {
            const iedResult = await this._getRightsEvaluator(context).evaluateIEDRights(combinedRightType, this._ownerForm, this._frame, usesFormRights, cancellationToken);
            //const iedResult = await this._evaluateIEDRights(combinedRightType, context, usesFormRights, cancellationToken);

            if (evaluateInsert)
                this._frame.actualObjectStandardRights.canNew = iedResult.canNew;

            if (evaluateEdit) {
                this._frame.actualObjectStandardRights.canEdit = iedResult.canEdit;
                this._frame.actualObjectStandardRights.editItemRights = iedResult.editItemRights;
                this._frame.actualObjectStandardRights.contextEditRoleIds = iedResult.contextEditRoleIds;
            }

            if (evaluateDelete) {
                this._frame.actualObjectStandardRights.canDelete = iedResult.canDelete;
                this._frame.actualObjectStandardRights.deleteItemRights = iedResult.deleteItemRights;
                this._frame.actualObjectStandardRights.contextDeleteRoleIds = iedResult.contextDeleteRoleIds;
            }

            if (iedResult.strictlyDisabled) {
                this._frame.actualObjectStandardRights.strictlyDisabled = iedResult.strictlyDisabled;
            }

            return iedResult;
        }

        return null;
    }
    /**
     * Mapa atributů ze třídy IClassItemRights, které odpovídají daným typům práv
     * @returns {}
     */
    private get _rightTypesToItemRightsPropsMap(): Map<RightType, keyof IClassItemRights> {
        if (!this.__rightTypesToItemRightsPropsMap)
            this.__rightTypesToItemRightsPropsMap = new Map([
                [RightType.visible, "Visible" as keyof IClassItemRights],
                [RightType.enabled, "Enabled" as keyof IClassItemRights],
                [RightType.required, "Required" as keyof IClassItemRights],
                [RightType.postponedRequired, "PostponedRequired" as keyof IClassItemRights],
                [RightType.recommended, "Recommended" as keyof IClassItemRights],
                [RightType.closeForm, "closeForm" as keyof IClassItemRights],
                [RightType.confirmIf, "confirm" as keyof IClassItemRights],
                [RightType.openAsEnabled, "OpenAsEnabled" as keyof IClassItemRights],
                [RightType.useDecryption, "useDecryption" as keyof IClassItemRights],
                [RightType.useDigitalSign, "useDigitalSign" as keyof IClassItemRights],
                [RightType.useEncryption, "useEncryption" as keyof IClassItemRights],
                [RightType.validateIf, "validate" as keyof IClassItemRights],
            ]);

        return this.__rightTypesToItemRightsPropsMap;
    }

    private _isParentDisabled(controlWrapper: ILogicalComponent): boolean {
        return (controlWrapper.parentControlWrapper?.control instanceof IBaseControlRights
            && (controlWrapper.parentControlWrapper?.control.enabled === false
                || controlWrapper.parentControlWrapper?.control.visible === false)
            && !(controlWrapper.parentControlWrapper?.control instanceof IFrameWithRights));
    }

    /**
     * Vrací komponentu, ke které se vztahují práva, například pro XGButton vrátí XGAction
     * @param controlWrapper
     * @returns
     */
    private static _getRightsControlFromWrapper(controlWrapper: ILogicalComponent): IGenericControl | IAction {
        if (controlWrapper.control instanceof IControlWithXGAction) {
            return controlWrapper.control.xgAction;
        }
        return controlWrapper.control;
    }

    //TODO-rites: obecná metoda(konkrétní implementace tohoto RightsManageru) pro vyhodnocování práv na uzlech stromu; bude "visitorem" při procházení stromu
    private async _frameRightsEvaluator(controlWrapper: ILogicalComponent, rightDefinitions: ICancellableControlRightDef[], contexts: IRightsContexts | null, evaluationProcessDetails: IGlobalEvaluationProcessDetails): Promise<IComponentTreeVisitorResult> {
        //eval(RightsDebugProbe.checkPoint(RightsDebugProbe.supportedCheckPointNamesEnum.frameRightsEvaluatorBeginning));

        const basicRightResults: BasicRightResults = new BasicRightResults();

        const getRightForType = (rightType: RightType): boolean | null => {
            let result: boolean | null;

            if (basicRightResults)
                result = basicRightResults.get(rightType); // Pokusí se vzít hodnotu práva z objektu základního vyhodnocení práv

            if (result != null) // Pokud existuje, vrací tuto hodnotu
                return result;
            return controlWrapper.getRight(rightType); // Jinak vrací hodnotu přímo z komponenty
        };

        const setRightForType = (rightType: RightType, valueToSet: boolean) => {
            if (valueToSet != null) {
                basicRightResults.set(rightType, valueToSet);
            }
        };
        const setAdditionalInfoForType = (infoType: AdditionalRightResultInfoType, value: boolean = true) => {
            if (value != null) {
                basicRightResults.setAdditionalResultInfo(infoType, value);
            }
        };

        // Pro frame, na kterém nebyla spuštěna inicializace, má smysl pouze právo viditelnosti kvůli případnému zneviditelnění nadřízené komponenty
        if (this._frame && !this._frame.initializationStarted)
            rightDefinitions = rightDefinitions.filter(rightDef => rightDef.type === RightType.visible);

        // V případě, že rodič je ILogicalContainer a už se na něm vyhodnocuje právo viditelnosti a i my chceme vyhodnotit
        // viditelnost, počkáme na doběhnutí vyhodnocení předchozích práv viditelnosti.
        if (controlWrapper.parentControlWrapper instanceof ILogicalContainer) {
            if (rightDefinitions.some(rightDef => rightDef.type === RightType.visible)) {
                await controlWrapper.parentControlWrapper.waitForRightTypeEvaluation(RightType.visible);
            }
        }

        //#region SetInvisibleIfInvisibleParent
        // Pokud je nadřízený kontejner neviditelný, zneviditelníme i tuto komponentu a dál s vyhodnocováním nepokračujeme
        if (rightDefinitions.some(def => def.type === RightType.visible) && controlWrapper.parentControlWrapper && controlWrapper.parentControlWrapper.getRight(RightType.visible) === false) {
            //controlWrapper.setRight(RightType.visible, false);
            setRightForType(RightType.visible, false);
            setAdditionalInfoForType(AdditionalRightResultInfoType.invisibleFromParent);
            return { result: true, basicRightResults };
        }
        //#endregion SetInvisibleIfInvisibleParent

        const control = RightsManager._getRightsControlFromWrapper(controlWrapper);
        const { controlContext, controlFrame } = this._getControlContext(controlWrapper, contexts);
        const classItemInfo = this._getControlClassItemInfo(controlWrapper);

        const itemRightsMapToEvaluate = ComponentRightsExtractor.getValidItemRightsForRightTypesMap(controlWrapper.rightsCollection, rightDefinitions.map(def => def.type), await this._getActiveRoleIds()/*, true*/);
        let serverRightsEvaluationResult: IClassItemRights | null;

        //#region RightTypeSpecificRules
        for (const rightDef of rightDefinitions) {
            if (!controlWrapper.isEvaluableForRight(rightDef.type))
                continue;
            const rightType = rightDef.type;
            const itemRightsForLevelsToEvaluate = itemRightsMapToEvaluate.get(rightType);
            let rightContext: IRightsContext = controlContext;
            const rightsEvaluator = this._getRightsEvaluator(rightContext);
            const formRightsToEvaluate: ItemRightsForRightLevels = new ItemRightsForRightLevels();
            // Vynechá vyhodnocení např. v případě, že se použije jen showIfDefault
            let skipEvaluation = false;
            let evaluateWithoutEditationRights = false;
            // Značí, zda se má komponenta podřídit editovatelnosti záznamu
            //let inheritEnabledFromEditability: boolean | null = null;
            let rightResult: boolean = null; //TODO-rites: ~jakou defaultní hodnotu??

            // let localAnonym = () => {
            //     let io = 1;
            //     io = io + io;
            //     io = io + rightType;
            // }
            // localAnonym();

            // let localFun = function () {
            //     let io = 1;
            //     io = io + io;
            // }
            // localFun();

            //let [fun, param] =
            //eval(RightsDebugProbe.checkPoint(RightsDebugProbe.supportedCheckPointNamesEnum.frameRightsEvaluatorRightTypeBeforeSwitch/*, { controlWrapper, rightType, contexts, evaluationProcessDetails }*/));
            //(() => {fun(param)})();

            switch (rightType) {
                case RightType.insert:
                case RightType.edit:
                case RightType.delete:
                    // TODO-rites: Nedělat nic? Nechat na overridech?; c2: asi ne, blbě by se v nich určovalo, že se má nastavit jinej typ práva...nebo ne?...; c3: zatím je v overrides a vypadá to dobře; c4: Pro edit a delete asi nechceme skipovat?; c5: chceme
                    //if (control instanceof IAction && this._frame instanceof IMasterableFrame && control.actionType === ActionType.standardDeleteRecord) {
                    //    control.enabled = this._frame.hasSelectedRecord && this._frame.actualObjectStandardRights.canDelete && this._frame.isActive;
                    //}
                    skipEvaluation = true;
                    break;
                case RightType.visible:
                    // TODO-rites: #Pokud je rightsEvaluationResult a je false, tak už se nemusí vyhodnocovat podmínka

                    // Pro viditelnost potřebuji u framu kontext z nadřízeného framu
                    if (controlWrapper.control instanceof IFrameWithRights) {
                        if (contexts.previousContext)
                            rightContext = contexts.previousContext;
                        else if (controlWrapper.control instanceof IMasterableFrame) { // Pokud se spustilo vyhodnocení až na podřízeném framu, tak previousContext nemusí být naplněn, proto se vezme masterObject, pokud je naplněn
                            rightContext = controlWrapper.control.masterObject;
                        } else if (controlWrapper.control instanceof IDetailFrame) {
                            rightContext = controlWrapper.control.masterFrame?.masterObject;
                        }
                    }

                    /**
                     * Pokud byl objekt změněn na serveru a uživatel na něj ztratí práva, tak stále je chtěné zobrazit prvky formuláře.
                     * Bug #38084: Nezobrazení listframu
                     */
                    if (rightContext) {
                        if (rightContext.roleIds?.length === 0 && rightContext?.id == "-666") {
                            rightResult = true;
                            skipEvaluation = true;
                            break;
                        }
                    }
                    //Pokud není nadřízená komponenta viditelná přebere právo z nadřízené komponenty
                    if (controlWrapper.parentControlWrapper?.control instanceof IBaseControlRights // TODO-rites: Už je řešeno výše v této metodě. Prověřit, tady již asi není potřeba
                        && controlWrapper.parentControlWrapper?.control.visible === false) {
                        rightResult = false;
                        setAdditionalInfoForType(AdditionalRightResultInfoType.invisibleFromParent);
                        skipEvaluation = true;
                        break;
                    }

                    // Pokud není vybrán objekt a komponenta má nastavený ShowIf, tak se použije showIfDefault
                    if (!RightsManager._canEvaluateFormDefinitionCondition(controlWrapper, rightContext, itemRightsForLevelsToEvaluate)) {
                        rightResult = controlWrapper.rightsCollection.showIfDefault;
                        skipEvaluation = true;
                        if (!rightResult)
                            break;
                    }

                    this._debugPrintMethod(`"Evaluating" visibility element ided: ${controlWrapper.control.element?.getAttribute("id")}`, 100);
                    break;
                case RightType.enabled: {
                    // Pro standardní akce se přeskakuje a nechává se to na RightsEvaluationOverrides
                    if (controlWrapper.control instanceof StandardAction) {
                        skipEvaluation = true;
                        break;
                    }

                    if ((!rightContext && controlWrapper.componentType === ComponentType.methodComponent && !controlWrapper.isStatic) ||
                        !RightsManager._canEvaluateFormDefinitionCondition(controlWrapper, rightContext, itemRightsForLevelsToEvaluate)) {
                        rightResult = false;
                        skipEvaluation = true;
                        break;
                    }

                    // Detail form akce (FormButton), tiskové akce a akce s nastaveným RunWithoutEdit nikdy neřeší právo editace
                    if (control instanceof IRunWithoutEdit && control.runWithoutEdit || control instanceof DetailFormAction || control instanceof ReportAction) {
                        evaluateWithoutEditationRights = true;
                        break;
                    }

                    //Pokud není nadřízená komponenta viditelná nebo aktivní, přebere právo z nadřízené komponenty
                    if (this._isParentDisabled(controlWrapper)) {
                        rightResult = false;
                        setAdditionalInfoForType(AdditionalRightResultInfoType.disabledFormParent);
                        skipEvaluation = true;
                        break;
                    }

                    // Pokud není komponenta editační, není to frame s právy ani akce s možností runwithoutedit, tak nechceme vyhodnocovat práva editace z objektu (typicky nějaký kontejner - panel, záložka)
                    if (!(control instanceof IFrameWithRights || control instanceof IEditableControl || control instanceof IRunWithoutEdit)) {
                        evaluateWithoutEditationRights = true;
                        break;
                    }

                    this._debugPrintMethod(`"Evaluating" enabled element ided: ${controlWrapper.control.element?.getAttribute("id")}`, 100);
                    // TODO-rites: pokud není komponenta viditelná..chceme nějak měnit enabled?...byť na defaultní? což by bylo true; c2: zatím nechceme, spíš by někdo mohlo způsobit nechtěnou aktivnost
                    break;
                }
                case RightType.required:
                    this._debugPrintMethod(`"Evaluating" required element ided: ${controlWrapper.control.element?.getAttribute("id")}`, 100);
                    // Pokud je komponenta neviditelná, nedostupná, nebo nelze vyhodnotit podmínku z form definice, pak se required nebude vyhodnocovat a jen se mu nastaví defaultní hodnota
                    if (getRightForType(RightType.visible) === false || getRightForType(RightType.enabled) === false || !RightsManager._canEvaluateFormDefinitionCondition(controlWrapper, rightContext, itemRightsForLevelsToEvaluate)) {
                        skipEvaluation = true;
                        rightResult = rightsEvaluator.getDefaultRightValueForRightType(rightType);
                    }
                    break;
                case RightType.postponedRequired:
                    // Pokud je komponenta neviditelná, nedostupná nebo povinná, nebo nelze vyhodnotit podmínku z form definice, pak se postponedRequired nebude vyhodnocovat a jen se mu nastaví defaultní hodnota
                    if (getRightForType(RightType.visible) === false || getRightForType(RightType.enabled) === false || getRightForType(RightType.required) || !RightsManager._canEvaluateFormDefinitionCondition(controlWrapper, rightContext, itemRightsForLevelsToEvaluate)) {
                        skipEvaluation = true;
                        rightResult = rightsEvaluator.getDefaultRightValueForRightType(rightType);
                    }
                    this._debugPrintMethod(`"Evaluating" postReq element ided: ${controlWrapper.control.element?.getAttribute("id")}`, 100);
                    break;
                case RightType.recommended:
                    // Pokud je komponenta neviditelná, nedostupná, povinná nebo odloženě povinná, nebo nelze vyhodnotit podmínku z form definice, pak se recommended nebude vyhodnocovat a jen se mu nastaví defaultní hodnota
                    // Právo má smysl jen pro editovatelný frame, který je v editaci
                    if (getRightForType(RightType.visible) === false || getRightForType(RightType.enabled) === false || getRightForType(RightType.required) || getRightForType(RightType.postponedRequired) || !(this._frame instanceof IEditableFrame) || !this._frame.isEditing || !RightsManager._canEvaluateFormDefinitionCondition(controlWrapper, rightContext, itemRightsForLevelsToEvaluate)) {
                        skipEvaluation = true;
                        rightResult = rightsEvaluator.getDefaultRightValueForRightType(rightType);
                    }
                    this._debugPrintMethod(`"Evaluating" recommended element ided: ${controlWrapper.control.element?.getAttribute("id")}`, 100);
                    break;
                case RightType.openAsEnabled:
                    this._debugPrintMethod(`"Evaluating" openAsEnabled element ided: ${controlWrapper.control.element?.getAttribute("id")}`, 100);
                    break;
                case RightType.editing:
                case RightType.interactive: //TODO-rites: ? nahradit propertou "isActive"???
                    this._debugPrintMethod(`"Evaluating" '${RightType[rightType]}' element ided: ${controlWrapper.control.element?.getAttribute("id")}`, 100);

                    if (this._isParentDisabled(controlWrapper)) {
                        setAdditionalInfoForType(AdditionalRightResultInfoType.disabledFormParent);
                    }
                    skipEvaluation = true; // TODO-rites: pouze přeskočit a nechat na overrides?
                    break;
                case RightType.multiEditing:
                    //Pro multieditaci bude potřeba znovu vyhodnotit podmínky, jejich vyhodnocení se procesně liší od editace jednoho objektu
                    break;
                case RightType.enabledVisual:
                    //Pokud není nadřízená komponenta viditelná nebo aktivní, přebere právo z nadřízené komponenty
                    if (this._isParentDisabled(controlWrapper) || (controlWrapper.parentControlWrapper?.control instanceof IEnabledVisualControl
                        && controlWrapper.parentControlWrapper?.control.enabledVisual === false)) {
                        setAdditionalInfoForType(AdditionalRightResultInfoType.disabledFormParent);
                        rightResult = false;
                        skipEvaluation = true;
                        break;
                    }

                    //rightResult = getRightForType(RightType.enabled) && getRightForType(RightType.editing) && getRightForType(RightType.interactive);
                    skipEvaluation = true;
                    break;
                case RightType.confirmIf:
                case RightType.useDecryption:
                case RightType.useDigitalSign:
                case RightType.useEncryption:
                case RightType.validateIf:
                    // Pokud nelze vyhodnotit podmínku z form definice, pak se nebude vyhodnocovat a jen se nastaví defaultní hodnota.
                    if (!RightsManager._canEvaluateFormDefinitionCondition(controlWrapper, rightContext, itemRightsForLevelsToEvaluate)) {
                        skipEvaluation = true;
                        rightResult = rightsEvaluator.getDefaultRightValueForRightType(rightType);
                    }
                    break;
                default:
                    this._debugPrintMethod(`Missing basic right evaluation function for enum type ${RightType[rightType]}!`, 5); // ` TODO-rites: debug or keep?
                    break;
            }

            //eval(RightsDebugProbe.checkPoint(RightsDebugProbe.supportedCheckPointNamesEnum.frameRightsEvaluatorRightTypeAfterSwitch));

            //TODO: Pro multieditaci asi bude potřeba projít a zrevidovat
            if (!skipEvaluation) {
                if (classItemInfo && (rightContext || classItemInfo.type === ClassItemType.Method)) { // Pro (statickou) metodu vyhodnocujeme, i když nemáme objekt. U atributu bez objektu nevyhodnocujeme.
                    if (!serverRightsEvaluationResult) {
                        // The server ignores the rights type and always returns all rights, so it only needs to be called if no other right type called it.
                        serverRightsEvaluationResult = await rightsEvaluator.evaluateServerRight(classItemInfo, undefined, this._getRequestContext(rightDef.ct), (classItemInfo.type === ClassItemType.Method && controlWrapper.isStatic));
                    }

                    if (serverRightsEvaluationResult) {
                        if (!controlWrapper.classItemRights)
                            controlWrapper.classItemRights = { Name: classItemInfo.name, Type: classItemInfo.type };
                        for (const [key, value] of Object.entries(serverRightsEvaluationResult as IClassItemRights)) {
                            //@ts-ignore
                            controlWrapper.classItemRights[key as keyof IClassItemRights] = value;
                        }
                    }
                }

                //eval(RightsDebugProbe.checkPoint(RightsDebugProbe.supportedCheckPointNamesEnum.frameRightsEvaluatorRightTypeBeforeFormRightsAdd));

                //TODO: projít a popřemýšlet jestli by nešlo přesunout dod rightsEvaluatoru
                // Pro vyhodnocení práva editace potřebujeme přidat i práva formuláře
                if (rightType === RightType.enabled) {
                    const isContextNew = contexts?.context != null && contexts.context.isNew; // TODO-rites: Fakt by nešlo spojit do "contexts?.context?.isNew"?
                    if (!isContextNew) { // Pro nově vytvářený objekt vynecháváme
                        if (!evaluateWithoutEditationRights) {
                            // TODO-rites: ? odfiltrovat formItemRights, které nejsou v context.editRoleIds? nebo to udělat až hlouběji?
                            controlFrame?.actualObjectStandardRights.editItemRights?.forEach(formEditItemRights => formRightsToEvaluate.addItemRights(formEditItemRights)); // TODO-rites: !! tohle potom bude podmínky vyhodnocovat znovu; asi hlouběji nevyhodnocovat znovu (jak odlišit?)
                        } else {
                            //this._frame.actualObjectStandardRights.editItemRights?.forEach(formEditItemRights => formRightsToEvaluate.addItemRights(formEditItemRights)); // TODO-rites: !! tohle potom bude podmínky vyhodnocovat znovu; asi hlouběji nevyhodnocovat znovu (jak odlišit?)
                        }
                    }
                }

                //eval(RightsDebugProbe.checkPoint(RightsDebugProbe.supportedCheckPointNamesEnum.frameRightsEvaluatorRightTypeBeforeItemRightsEval));

                const additionalRightResultInfoFlags = new OutputParameter<AdditionalRightResultInfoType[]>();

                rightResult = await this._evaluateRightTypeOnItemRights(rightType, serverRightsEvaluationResult, formRightsToEvaluate, itemRightsForLevelsToEvaluate, evaluationProcessDetails.evaluationCache, rightContext, controlWrapper.isStatic, evaluateWithoutEditationRights/*, inheritEnabledFromEditability,*/, additionalRightResultInfoFlags, rightDef.ct);

                if (!rightResult && rightType === RightType.enabled) {
                    setAdditionalInfoForType(AdditionalRightResultInfoType.disabledByItemRights);
                }
            }
            // else {
            //     setAdditionalInfoForType(AdditionalRightResultInfoType.itemRightsEvaluationSkipped);
            // }

            if (rightResult != null)
                setRightForType(rightType, rightResult);
        }
        //#endregion RightTypeSpecificRules

        //#region DebugTestColors
        //Vyhodnocované komponenty barevně označuje

        //let oldBgColor: any;
        //let oldBgColorStored = false;
        //if (controlWrapper.control && controlWrapper.control.element && controlWrapper.control.element.style) {// TODO-rites: debug remove !!
        //    oldBgColor = controlWrapper.control.element.style.backgroundColor;
        //    oldBgColorStored = true;
        //    controlWrapper.control.element.style.backgroundColor = "green";// TODO-rites: debug remove !!
        //}

        //return PromiseUtils.delay(500).then(() => {
        //    this._logger.log(`"Evaluated" element ided: ${controlWrapper.control.element.getAttribute("id")}`); // `
        //    if (oldBgColorStored) { // TODO-rites: debug remove !!
        //        controlWrapper.control.element.style.backgroundColor = oldBgColor; // TODO-rites: debug remove !!
        //    };
        //    return { result: true };
        //});
        //#endregion DebugTestColors

        const evaluationResult: IComponentTreeVisitorResult = { result: true, basicRightResults };

        if (rightDefinitions.some(def => def.type === RightType.visible)) {
            evaluationResult.visible = getRightForType(RightType.visible);
        }

        return evaluationResult; // TODO-rites: #vracet skutečný výsledek/úspěch?; c2: asi jestli úspěch. Při vyhodnocování několika typů práv nejde vrátit jeden výsledek..a asi dílčí ani nejsou potřeba
    }

    /**
     * Vrátí položku práv komponenty k vyhodnocení
     * @param controlWrapper Komponenta, která se vyhodnocuje.
     */
    private _getControlClassItemInfo(controlWrapper: ILogicalComponent): IClassItem | undefined {
        if (controlWrapper.componentType === ComponentType.attributeComponent) {
            const name = controlWrapper.attrName;
            if (!name) {
                this._logger.error("Trying to evaluate rights on attributeComponent, but no attrName was supplied!");
                return undefined;
            }
            return { name, type: ClassItemType.Attribute };
        }

        if (controlWrapper.componentType === ComponentType.methodComponent) {
            let name = controlWrapper.methodName;
            if (!name) {
                return undefined;
            }
            const rightControl = RightsManager._getRightsControlFromWrapper(controlWrapper);
            if (!name.includes(".") && controlWrapper.isStatic && rightControl instanceof XGAction /*TODO-rites: nahradit ifacem*/) {
                // Pokud je akce na neviditelném framu, tak neproběhla inicializace a není naplněn owner, tak v tom případě se vezme objectName z RightsManagera.
                const objectName = rightControl.executionContext.ownerFrame?.objectName || rightControl.executionContext.actualObject?.objectName;
                if (objectName) {
                    name = `${objectName}.${name}`;
                }
            }
            return { name, type: ClassItemType.Method };
        }
        return undefined;
    }

    /**
     * Vrátí context a frame, ke kterému se komponenta vztahuje
     * @param controlWrapper Komponenta, která se vyhodnocuje.
     * @param contexts Kontexty, ke kterým se komponenta může vztahovat.
     */
    private _getControlContext(controlWrapper: ILogicalComponent, contexts: IRightsContexts): { controlContext: IRightsContext; controlFrame: IFrameWithRights } {
        if (controlWrapper.useMasterObject) {
            return {
                controlFrame: this._frame.masterFrame,
                controlContext: contexts.previousContext ?? this._frame.masterObject,
            };
        }
        return {
            controlFrame: this._frame,
            controlContext: contexts.context,
        };
    }

    /**
     * Zjišťuje, zda lze vyhodnotit podmínky z definice formuláře.
     * @param controlWrapper Komponenta, která se vyhodnocuje.
     * @param context Aktuální objekt vyhodnocení.
     * @param itemRights Práva komponenty.
     * @returns Vrací true pokud context není prázdný nebo komponenta neobsahuje podmínku, kterou bez contextu nelze vyhodnotit.
     */
    private static _canEvaluateFormDefinitionCondition(controlWrapper: ILogicalComponent, context: IRightsContext, itemRights: ItemRightsForRightLevels): boolean {
        if (context || (controlWrapper.componentType === ComponentType.methodComponent && controlWrapper.isStatic))
            return true;

        return !itemRights.flattenIncludingOnly(RightLevel.formDefinitionLevel, RightLevel.formLevel, RightLevel.allRolesFormLevel).some(item => item.conditionID);
    }

    /**
     * Vyhodnotí výsledek daného typu práva a vrátí výsledek vyhodnocení
     * @param rightType
     * @param serverClassItemRights
     * @param formRightsToEvaluate
     * @param itemRightsForLevelsToEvaluate
     * @param cachedConditions
     * @param context
     * @param isStatic
     * @param evaluateWithoutEditationRights
     * @param _additionalRightResultInfoFlagsParam
     * @param ct
     */
    // TODO-rites: NOTE: na konci souboru menší UT
    private async _evaluateRightTypeOnItemRights(rightType: RightType, serverClassItemRights: IClassItemRights, formRightsToEvaluate: ItemRightsForRightLevels,
        itemRightsForLevelsToEvaluate: ItemRightsForRightLevels, cachedConditions: Map<string, Promise<IEvaluatedConditionResult>>, context: IRightsContext | null, isStatic: boolean | null,
        evaluateWithoutEditationRights: boolean, /* inheritEnabledFromEditability: boolean | null,*/
        _additionalRightResultInfoFlagsParam: OutputParameter<AdditionalRightResultInfoType[]>,
        ct: CancellationToken): Promise<boolean> {
        let valueToSet: boolean | null = null;

        // Získá hodnotu daného typu práva z objektu práv
        const getClassItemRightsByRightType = (rightType: RightType, classItemRights: IClassItemRights): boolean | null => {
            let result: boolean | null = null;

            if (classItemRights != null) {
                const propertyKey: PropertyKey = this._rightTypesToItemRightsPropsMap.get(rightType);
                if (!propertyKey)
                    return null;

                result = ObjectUtils.dynamicGetter(classItemRights, propertyKey);
            }

            return result;
        };
        //TODO: V případě multieditace prunik editačních roli? co pro visible, required atd?
        const classItemRightsResult = getClassItemRightsByRightType(rightType, serverClassItemRights);
        //if (classItemRightsResult != null)
        //    valueToSet = classItemRightsResult;

        const defaultRightValueForRightType = this._getRightsEvaluator(context).getDefaultRightValueForRightType(rightType);

        //#region interfaceLevelDraft //TODO-rites: ? Tohle nepůjde. Minimálně kvůli multiEditaci. Toto rozhodnutí (o zkráceném vyhodnocení) musíme nechat až na další fáze vyhodnocení
        if (classItemRightsResult != null && defaultRightValueForRightType !== classItemRightsResult)
            return classItemRightsResult; // Pokud ze serveru přijde jiná, než defaultní hodnota, formulářová úroveň ji již nemůže změnit a vracíme výsledek
        //#endregion interfaceLevelDraft

        //if (valueToSet == null) // TODO-rites: #nastavit defaultní hodnotu, pokud žádnou nemám
        //    valueToSet = defaultRightValueForRightType;

        let passingRoleIds;

        if (rightType === RightType.enabled) {
            const roleIds = (evaluateWithoutEditationRights || (context && context.isNew)) ? context?.roleIds : context?.editRoleIds; // Pokud je nastaven příznak "evaluateWithoutEditationRights" nebo se jedná o nový objekt, potom se místo editačních rolí použijí role čtení; Typické využití je při RunWithoutEdit
            passingRoleIds = roleIds && [...roleIds];

            if (formRightsToEvaluate && formRightsToEvaluate.size > 0) {
                //#region RoleLess
                const roleLessFormRights = formRightsToEvaluate.flattenExcluding(RightLevel.formLevel); // Vrací ItemRights z DB "bez role" pro formulář a z definice formuláře

                if (roleLessFormRights.length > 0) {
                    const roleLessResult = (await this._getRightsEvaluator(context).evaluateLevelSubsetItemRights(rightType, roleLessFormRights, cachedConditions, isStatic, this._frame, ct)).one;

                    if (roleLessResult != null && roleLessResult !== defaultRightValueForRightType)
                        return roleLessResult;
                }
                //#endregion RoleLess

                //#region Roles
                const formRightsForRoles = formRightsToEvaluate.flattenIncludingOnly(RightLevel.formLevel); // Vrací formulářová ItemRights z DB

                let rolesResultTuple: Tuple<boolean | null, string[] | null> = null;
                if (formRightsForRoles.length > 0) {
                    rolesResultTuple = await this._getRightsEvaluator(context).evaluateLevelSubsetItemRights(rightType, formRightsForRoles, cachedConditions, isStatic, this._frame, ct, roleIds);

                    if (roleIds && roleIds instanceof Array) {
                        passingRoleIds = roleIds.filter(roleId => rolesResultTuple.two?.includes(roleId));
                    }
                }
                //#endregion Roles

                const rolesResult = rolesResultTuple?.one;
                // Při nedefaultní hodnotě práva na úrovni formuláře již nejde změnit právy na prvek/akci
                if (rolesResult != null && rolesResult !== defaultRightValueForRightType)
                    return rolesResult;
            }
        } else if (rightType === RightType.visible) {
            passingRoleIds = context?.roleIds && [...context.roleIds];
        } else if (rightType === RightType.required || rightType === RightType.postponedRequired || rightType === RightType.recommended) {
            const roleIds = context?.editRoleIds;
            passingRoleIds = roleIds && [...roleIds];
        } else {
            passingRoleIds = this._activeRoleIds && [...this._activeRoleIds];
        }

        if (context && passingRoleIds?.length <= 0) {
            const typeOperator = this._getRightsEvaluator(context).getOperatorForRightType(rightType);
            if (classItemRightsResult)
                valueToSet = classItemRightsResult;
            else
                valueToSet = typeOperator === LogicalOperator.or ? !defaultRightValueForRightType : false;
        }

        //rozšíření práv prvků o implicitní práva:
        if (itemRightsForLevelsToEvaluate) {
            //bez uvedení role
            let levelRights = itemRightsForLevelsToEvaluate.get(RightLevel.allRolesFormLevel);
            if (!levelRights) {
                const formRights = this._ownerFormRights?.find(r => r.level === RightLevel.allRolesFormLevel);
                if (formRights)
                    itemRightsForLevelsToEvaluate.addItemRights(formRights.getItemImplicitRight(rightType));
            }
            //implicitní práva k rolím
            levelRights = itemRightsForLevelsToEvaluate.get(RightLevel.formLevel);
            for (const right of this._ownerFormRights?.filter(r => r.level === RightLevel.formLevel) ?? []) {
                if (!levelRights || !levelRights.find(item => item.roleID === right.roleID)) {
                    itemRightsForLevelsToEvaluate.addItemRights(right.getItemImplicitRight(rightType));
                }
            }
        }

        if (valueToSet == null && itemRightsForLevelsToEvaluate && itemRightsForLevelsToEvaluate.size > 0) {
            //#region RightsFromDefinition
            const definitionItemRights = itemRightsForLevelsToEvaluate.flattenIncludingOnly(RightLevel.formDefinitionLevel); // Vrací ItemRights z definice formuláře

            if (definitionItemRights.length > 0) {
                const definitionResult = (await this._getRightsEvaluator(context).evaluateLevelSubsetItemRights(rightType, definitionItemRights, cachedConditions, isStatic, this._frame, ct)).one;

                if (definitionResult != null && definitionResult !== defaultRightValueForRightType)
                    return definitionResult;
            }
            //#endregion RightsFromDefinition

            //#region RoleLess
            const roleLessItemRights = itemRightsForLevelsToEvaluate.flattenIncludingOnly(RightLevel.allRolesFormLevel); // Vrací ItemRights z DB "bez role" pro formulář

            if (roleLessItemRights.length > 0) {
                const roleLessResult = (await this._getRightsEvaluator(context).evaluateLevelSubsetItemRights(rightType, roleLessItemRights, cachedConditions, isStatic, this._frame, ct)).one;

                if (roleLessResult != null && roleLessResult !== defaultRightValueForRightType)
                    return roleLessResult;
            }
            //#endregion RoleLess

            //#region Roles
            const itemRightsForRoles = itemRightsForLevelsToEvaluate.flattenIncludingOnly(RightLevel.formLevel); // Vrací ItemRights pro formulář z DB

            let rolesResultTriplet: Triplet<boolean | null, string[] | null, string[] | null> = null;
            if (itemRightsForRoles.length > 0) {
                // Protože v multieditaci nelze rozhodnout, zda jsou všechny objekty dočteny stejnými rolemi, tak je nutné omezit pouze na ty, které mají uvedeno právo na formulářové úrovni, pokud nějaké jsou.
                if (passingRoleIds && !evaluateWithoutEditationRights && this._ownerFormRights && context instanceof IMultiEditObject) {
                    passingRoleIds = ArrayUtils.intersect(this._ownerFormRights.filter(item => item.level === RightLevel.formLevel).map(item => item.roleID), passingRoleIds); // TODO-rites: Filtrovat ownerFormRights víc a rozlišovat pro čtení a pro editaci?
                }

                if (!(passingRoleIds && passingRoleIds.length <= 0)) //pokud vyšel průnik aktivních rolí a rolí z práv prázdný, nevyhodnocujeme - použije se default
                    rolesResultTriplet = await this._getRightsEvaluator(context).evaluateLevelSubsetItemRights(rightType, itemRightsForRoles, cachedConditions, isStatic, this._frame, ct, passingRoleIds);
            }
            //#endregion Roles

            if (passingRoleIds && passingRoleIds instanceof Array && rolesResultTriplet?.three && rolesResultTriplet.three instanceof Array) {
                passingRoleIds = ArrayUtils.difference(passingRoleIds, rolesResultTriplet.three);
            }

            valueToSet = rolesResultTriplet?.one;
        }

        if (valueToSet == null)
            valueToSet = defaultRightValueForRightType;

        return valueToSet;
    }

    /**
     * Vyhodnotí a aplikuje overrides dané komponenty
     * @param controlWrapper Zpracovávaný uzel stromu
     * @param rightDefinitions Definice práv k vyhodnocení
     * @param rightEvaluationOverrides Úpravy výsledku vyhodnocení práv
     * @param basicRightResults Výsledek základního průchodu vyhodnocení práv
     */
    private async _evaluateOverridesOnControl(controlWrapper: ILogicalComponent, rightDefinitions: ICancellableControlRightDef[],
        rightEvaluationOverrides: RightTypeEvaluationFunctionType[], basicRightResults: IBasicRightResults, context: IRightsContext): Promise<void> {
        // Může nastat případ, kdy je vyhodnocení práv naplánováno až po naplánování dispose. V takovém případě již nemá smysl práva vyhodnocovat, což zajistí následující kontrola.
        if (this._disposed) {
            return;
        }

        if (rightEvaluationOverrides == null && basicRightResults == null) // Pokud nemám overrides, ani základní výsledky vyhodnocení práv, nemá smysl pokračovat - nebylo by co nastavit
            return;

        //if (basicRightResults != null)
        //    dontUseBasicRightResults();

        for (const def of rightDefinitions) {
            if (def.ct.cancellationRequested)
                continue;

            const rightType = def.type;
            let combinedResult: boolean | null;

            if (rightEvaluationOverrides) {
                const overrideFunctions: (() => Promise<boolean> | boolean)[] = [];

                for (const rightOverride of rightEvaluationOverrides) {
                    if (rightType === rightOverride.rightType) {
                        overrideFunctions.push(() => /* TODO-rites: check - není potřeba await */ rightOverride.evaluationFunction(controlWrapper, basicRightResults && basicRightResults.get(def.type), basicRightResults));
                    }
                }

                if (overrideFunctions.length <= 0) {
                    if (basicRightResults == null)
                        continue;
                } else {
                    let logicalOperator = this._rightsEvaluationOverrides.getOperatorForRightType(rightType); // TODO-rites: #mít zvlášť metodu pro operátor nad overrides? (možná definován a přístupný přímo z nich?)

                    if (logicalOperator == null)
                        logicalOperator = this._getRightsEvaluator(context).getOperatorForRightType(rightType);

                    //eval(RightsDebugProbe.checkPoint(RightsDebugProbe.supportedCheckPointNamesEnum.evaluateOverridesOnControlRightType));
                    combinedResult = await LogicalOperations.binaryOperationOverAsyncFunctions(logicalOperator, overrideFunctions);
                }
            }

            if (combinedResult == null && basicRightResults) { // Pokud není k dispozici hodnota z overrides, použije se přímo hodnota ze základního výsledku vyhodnocení práv
                combinedResult = basicRightResults.get(rightType);
            }

            if (combinedResult != null) {
                //eval(RightsDebugProbe.checkPoint(RightsDebugProbe.supportedCheckPointNamesEnum.evaluateOverridesOnControlRightTypeBeforeSetToControlWrapper));
                controlWrapper.setRight(rightType, combinedResult);
            }
        }
    }

    /**
     * Predikát určující, zda se má pokračovat vyhodnocováním práv na potomcích právě zpracovávaného uzlu.
     * @param visitResult Výsledek návštěvy právě zpracovávaného uzlu
     */
    private static _shouldProcessChildren(visitResult: IComponentTreeVisitorResult): boolean {
        return !visitResult.cancel;// && visitResult.visible;
    }

    /**
     * Metoda, která se provede po vyhodnocení všech potomků uzlu.
     * Pokud jsou všichni potomci neviditelní, zneviditelní se i aktuální komponenta.
     * @param controlWrapper Zpracovávaný uzel stromu
     * @param childrenResult Výsledek návštěvy všech potomků
     * @param visitorResult Výsledek návštěvy právě zpracovávaného uzlu
     */
    private static _afterChildrenMethod(controlWrapper: ILogicalComponent, childrenResult: IBaseAsyncTreeChildrenResult, visitorResult: IComponentTreeVisitorResult | void): IComponentTreeVisitorResult | void {
        if (!childrenResult || !childrenResult.results || childrenResult.results.length <= 0)
            return visitorResult;

        let areAllChildrenInvisible: boolean = true;

        for (const childResult of childrenResult.results) {
            if (!(childResult instanceof IComponentTreeVisitorVisibleResult) || (childResult instanceof IComponentTreeVisitorVisibleResult && childResult.visible)) {
                areAllChildrenInvisible = false;
                break;
            }
        }

        if (areAllChildrenInvisible && visitorResult instanceof IComponentTreeVisitorVisibleResult) {
            controlWrapper.setRight(RightType.visible, false);
            visitorResult.visible = false;
        }

        return visitorResult;
    }

    /**
     * Vstupní metoda vyhodnocení daných typů práv
     * @param rightTypes Typy práv, které se mají vyhodnotit
     * @param context Kontext, vzhledem ke kterému se vyhodnocují práva
     * @param usesFormRights Formulářová práva by se měla aplikovat, pokud je frame hlavním framem nebo pokud je to detail k hlavnímu seznamu
     * @param priorityRightTypes Typy práv, které se mají prioritně vyhodnotit
     * @param foreignContextEvaluation Zda probíhá vyhodnocení práv v nestandardním kontextu
     */
    public evaluateRights(rightTypes: RightType[], context: IRightsContext | null, usesFormRights?: boolean, priorityRightTypes?: RightType[], foreignContextEvaluation?: boolean): Promise<void> {
        const evaluationFunction = async (rightDefinitions: ICancellableControlRightDef[], context: IRightsContext | null, ct: CancellationToken, priorityRightDefinitions?: ICancellableControlRightDef[]): Promise<void> => {
            const riedRightTypes = rightDefinitions.filter(def => def.type & (RightType.read | RightType.insert | RightType.edit | RightType.delete)).map(def => def.type);

            if (riedRightTypes && riedRightTypes.length > 0) {
                //TODO-rites: ~eval RIED laws!
                if (await this._evaluateAndApplyIEDRights(riedRightTypes, context, usesFormRights, ct) != null) {
                    if (!this.initialized) {
                        // Vyhodnotí se CRUD práva, ale ještě nebyl vytvořen strom komponent, takže se na komponenty tato práva hned neuplatní.
                        // Ale uplatní se na ně až když jsou komponenty vytvořeny a znovu se na nich spustí nastavení práv.
                        // To je v podstatě v pořádku, výsledek těchto práv se hned nastaví framu a on ho hned používá pro další rozhodování.
                        this._debugPrintMethod("CRUD rights evaluated but hasn't been initialized yet! Skipping applying results to component tree.", 5);
                    }
                }
            }

            if (this.initialized) {
                try {
                    this._runningEvaluationsCount++;
                    this.rightsEvaluating.trigger(this, context);
                    await this._evaluateRightsOnComponentTree(rightDefinitions, context, ct, priorityRightDefinitions, foreignContextEvaluation);
                } catch (e) {
                    if (!(e instanceof CancelError))
                        this._logger.error("There was an error while evaluating rights.", e);
                } finally {
                    this._runningEvaluationsCount--;
                    this.rightsEvaluated.trigger(this, context);
                }
            }
        };

        return this._scheduler.schedule(rightTypes, context, evaluationFunction, priorityRightTypes, this._frame.uuid);
    }

    /**
     * Spouští vyhodnocení daných typů práv nad stromem komponent
     * @param rightDefinitions
     * @param context Kontext, vzhledem ke kterému se vyhodnocují práva
     * @param ct
     * @param priorityRightDefinitions
     * @param foreignContextEvaluation Zda probíhá vyhodnocení práv v nestandardním kontextu
     */
    private async _evaluateRightsOnComponentTree(rightDefinitions: ICancellableControlRightDef[], context: IRightsContext, ct: CancellationToken, priorityRightDefinitions?: ICancellableControlRightDef[], foreignContextEvaluation: boolean = false): Promise<void> {
        if (!this.initialized) {
            throw new InvalidOperationException("RightsManager hasn't been initialized yet!");
        }

        // Pokud chceme vyhodnocovat některé z práv enabled/interactive/editing, musíme následně vyhodnotit i právo enabledVisual - pokud není obsaženo, přidáme
        if (rightDefinitions.some(rightDefinition => (rightDefinition.type & (RightType.enabled | RightType.interactive | RightType.editing)) !== 0)) {
            if (!rightDefinitions.some(rightDefinition => (rightDefinition.type === RightType.enabledVisual))) {
                rightDefinitions.push({ type: RightType.enabledVisual, ct: CancellationToken.none });
            }
        }

        // Setřídí žádané typy práv podle priority(dána pořadím flagů v enumu), podle které je chceme vyhodnocovat
        rightDefinitions.sort((itemA: ICancellableControlRightDef, itemB: ICancellableControlRightDef) => {
            return itemA.type - itemB.type;
        });
        // Pokud byla přidána práva, která mají být vyhodnocena přednostně, přidají se na začátek setříděného pole rightDefinitions.
        if (priorityRightDefinitions && priorityRightDefinitions.length > 0) {
            rightDefinitions = priorityRightDefinitions.concat(rightDefinitions);
        }

        // Na všech kontejnerových komponentách ve stromě pro každý typ práva z rightDefinitions
        // vytvoříme pending promise, pokud neexistuje nebo je aktuální settled.
        await this._componentTree.traverseRootsFinishFirstAsync(
            async (controlWrapper) => {
                if (controlWrapper instanceof ILogicalContainer) {
                    rightDefinitions.forEach(rightDefinition => controlWrapper.tryToCreatePendingRightEvaluation(rightDefinition.type));
                }
            },
        );

        let depth = 0;
        const globalEvaluationProcessDetails: IGlobalEvaluationProcessDetails = {
            frameRightsEvaluatorsMap: new Map<ILogicalComponent, FrameRightsEvaluatorType>(),
            evaluationCache: new Map<string, Promise<IEvaluatedConditionResult>>(),
        };

        await this._componentTree.traverseRootsFinishFirstAsync(
            async (controlWrapper: ILogicalComponent, _node: IComponentTree<ILogicalComponent, ComponentType>, evaluationProcessDetails?: IEvaluationProcessDetails): Promise<IComponentTreeVisitorResult> => {
                if (ct.cancellationRequested) {
                    this._debugPrintMethod("Cancelling rights evaluation!", 5);
                    return {
                        cancel: true,
                        traverseDetails: evaluationProcessDetails,
                    };
                }

                if (controlWrapper.isVirtualControl || controlWrapper.rightsIgnored) { // Virtuální komponentu nemá smysl vyhodnocovat
                    return {
                        result: true,
                        cancel: controlWrapper.stopRightsEvaluatingOnChildren,
                        traverseDetails: evaluationProcessDetails,
                    };
                }

                // V případě, že má element atribut "data-row-datakey", chceme na něm práva vyhodnotit
                // pouze v případě, že se hodnota atributu rovná aktuálnímu id kontextu.
                const rowDataKeyAttribute = controlWrapper.control.element.getAttribute("data-row-datakey") || controlWrapper.control.element.parentElement?.getAttribute("data-row-datakey");
                if (rowDataKeyAttribute && rowDataKeyAttribute !== context?.id && rowDataKeyAttribute !== context?.instanceID) {
                    return {
                        cancel: true,
                        traverseDetails: evaluationProcessDetails,
                    };
                }

                // Používá se pro vyhodnocení práv pouze editačních komponent v nestandardním kontextu.
                if (foreignContextEvaluation) {
                    // Pokud se jedná o DetailFrame nebo Akci, tady skončíme.
                    if (controlWrapper.control instanceof IDetailFrame || controlWrapper.control instanceof IAction) {
                        return {
                            cancel: true,
                            traverseDetails: evaluationProcessDetails,
                        };
                    }
                }

                ++depth;
                this._debugPrintMethod("Entering depth " + depth, 100, depth);

                let currentEvaluationProcessDetails: IEvaluationProcessDetails | null;
                let currentFramesRightsManager: RightsManagerType | null = null;

                let frameRightsEvaluator: FrameRightsEvaluatorType;
                if (controlWrapper.control instanceof IFrameWithRights) {
                    if (evaluationProcessDetails?.currentFrameRightsEvaluator) {
                        globalEvaluationProcessDetails.frameRightsEvaluatorsMap.set(controlWrapper, evaluationProcessDetails.currentFrameRightsEvaluator);
                        this._debugPrintMethod(`Pushing parent's evaluator of element ${controlWrapper.control?.element?.getAttribute("data-tsccontrol")} id ${controlWrapper.control?.element?.id} to frameRightsEvaluatorsMap.`, 10); // ` TODO-rites: debug, remove
                    }

                    const newStack = new TransparentStack(evaluationProcessDetails?.stackOfFramesContexts?.getIterator());
                    // Pokud narazíme při vyhodnocování na jiný frame, pushneme na zásobník jeho kontext, customContext nebo actualObject.
                    if (controlWrapper.control === this._frame) {
                        newStack.push(context);
                    } else if (controlWrapper.control instanceof IComponentWithCustomEvaluationContext) {
                        newStack.push(await controlWrapper.control.getComponentCustomEvaluationContext());
                    } else {
                        newStack.push(controlWrapper.control.actualObject);
                    }

                    currentFramesRightsManager = controlWrapper.control.rightsManager;
                    frameRightsEvaluator = currentFramesRightsManager.getFrameRightsEvaluator();

                    currentEvaluationProcessDetails = {
                        stackOfFramesContexts: newStack,
                        currentFrameRightsEvaluator: frameRightsEvaluator,
                    };

                    this._debugPrintMethod(`Created evaluator of element ${controlWrapper.control?.element?.getAttribute("data-tsccontrol")} id ${controlWrapper.control?.element?.id} (${frameRightsEvaluator ? "not null" : "null"}).`, 10); // ` TODO-rites: debug, remove
                } else {
                    // Pro ostatní se použije dříve nastavený(rodičem) evaluator
                    frameRightsEvaluator = evaluationProcessDetails.currentFrameRightsEvaluator;
                    currentEvaluationProcessDetails = evaluationProcessDetails;
                }
                if (!frameRightsEvaluator) {
                    throw new InvalidOperationException("Missing frameRightsEvaluator!");
                }

                if (!currentEvaluationProcessDetails) {
                    currentEvaluationProcessDetails = {
                        stackOfFramesContexts: new TransparentStack(),
                        currentFrameRightsEvaluator: frameRightsEvaluator,
                    };
                }

                let customContext;
                // Pokud se jedná o komponentu s custom kontextem, přidáme ho do stacku.
                if (controlWrapper.control instanceof IComponentWithCustomEvaluationContext && !(controlWrapper.control instanceof IFrameWithRights)) {
                    customContext = await controlWrapper.control.getComponentCustomEvaluationContext();
                    if (customContext) {
                        currentEvaluationProcessDetails.stackOfFramesContexts.push(customContext);
                    }
                }

                // Pokud přecházíme na vyhodnocení práv dalšího framu, musíme vyhodnocovací funkci navázat příslušný RightsManager
                if (currentFramesRightsManager) { // TODO-rites: je opravdu potřeba??
                    frameRightsEvaluator = frameRightsEvaluator.bind(currentFramesRightsManager);
                }

                if (foreignContextEvaluation) {
                    // Pokud se jedná o MasterableFrame, přeskočíme ho.
                    if (controlWrapper.control instanceof IMasterableFrame) {
                        // Nový kontext už není nestandardní
                        foreignContextEvaluation = false;
                        // Pro každý typ práva z rightDefinitions resolvneme promise, protože už se vyhodnocení těchto práv dokončilo.
                        // TODO: Tohle je z neznámeho důvodu potřeba jenom ve verzi 22.01. V ostatních verzích to funguje dobře i bez následujících třech řádků. /-o.o-/
                        if (controlWrapper instanceof ILogicalContainer) {
                            rightDefinitions.forEach(rightDefinition => controlWrapper.resolvePendingRightTypeEvaluation(rightDefinition.type));
                        }
                        return {
                            result: true,
                            traverseDetails: currentEvaluationProcessDetails,
                        };
                    }
                }

                const evaluationResult = await (frameRightsEvaluator(controlWrapper, rightDefinitions, RightsManager._buildRightsContextsObject(currentEvaluationProcessDetails.stackOfFramesContexts), globalEvaluationProcessDetails, ct));
                //eval(RightsDebugProbe.checkPoint(RightsDebugProbe.supportedCheckPointNamesEnum.evaluateRightsOnComponentTreeBeforeOverrides));

                // Pokud se jedná o frame, který byl zakázaný nadřízenou komponentou, tak se tato informace bude propagovat na celý zbytek jeho větve.
                if (controlWrapper.control instanceof IFrameWithRights && evaluationResult.basicRightResults.getAdditionalResultInfo(AdditionalRightResultInfoType.disabledFormParent)) {
                    currentEvaluationProcessDetails.disabledByParent = true;
                } else if (currentEvaluationProcessDetails.disabledByParent) {
                    evaluationResult.basicRightResults.setAdditionalResultInfo(AdditionalRightResultInfoType.disabledFormParent, true);
                }

                if (evaluationResult.cancel !== true) {
                    await this._evaluateOverridesOnControl(controlWrapper, rightDefinitions, controlWrapper.rightsEvaluationOverrides, evaluationResult && evaluationResult instanceof IComponentTreeVisitorResult && evaluationResult.basicRightResults, context);
                }

                // Pokud se jedná o komponentu s custom kontextem, odstraníme ho ze stacku.
                if (customContext) {
                    currentEvaluationProcessDetails.stackOfFramesContexts.pop();
                }

                // Pro každý typ práva z rightDefinitions resolvneme promise, protože už se vyhodnocení těchto práv dokončilo.
                if (controlWrapper instanceof ILogicalContainer) {
                    rightDefinitions.forEach(rightDefinition => controlWrapper.resolvePendingRightTypeEvaluation(rightDefinition.type));
                }

                evaluationResult.traverseDetails = currentEvaluationProcessDetails;
                if (controlWrapper.stopRightsEvaluatingOnChildren) {
                    evaluationResult.cancel = true;
                }

                return evaluationResult;
            },
            undefined,
            RightsManager._shouldProcessChildren,
            undefined,
            (controlWrapper: ILogicalComponent, childrenResult: IBaseAsyncTreeChildrenResult, visitorResult: IBaseAsyncTreeVisitorResult | void) => {
                this._debugPrintMethod("Exiting depth " + depth, 100, depth);
                --depth;
                return RightsManager._afterChildrenMethod(controlWrapper, childrenResult, visitorResult);
            },
            (controlWrapper: ILogicalComponent) => {
                if (controlWrapper.control instanceof IFrameWithRights) {
                    const storedFrameEvaluator = globalEvaluationProcessDetails.frameRightsEvaluatorsMap.get(controlWrapper);
                    if (storedFrameEvaluator) {
                        globalEvaluationProcessDetails.frameRightsEvaluatorsMap.delete(controlWrapper);
                        this._debugPrintMethod(`Poping evaluator of element ${controlWrapper.control?.element?.getAttribute("data-tsccontrol")} id ${controlWrapper.control?.element?.id} from frameRightsEvaluatorsMap.`, 10); // TODO-rites: debug, remove
                    } else {
                        this._debugPrintMethod(`Evaluator of element ${controlWrapper.control?.element?.getAttribute("data-tsccontrol")} id ${controlWrapper.control?.element?.id} is not in frameRightsEvaluatorsMap!`, 10); // TODO-rites: ?! How can this happen?? Tohle se stane jednoduše, protože na stack se ukládají až evaluátory rodiče při příchodu na potomka. Takže u hlavního rodiče nikdy není co popnout. rodiče?! debug, remove
                    }
                }
            },
            ct,
        );
    }

    protected _getRequestContext(cancellationToken: CancellationToken = CancellationToken.none): RequestContext {
        return convertContextFromFrameToRequest(this._frame?.frameContext, cancellationToken);
    }

    //TODO-rites: debug remove?
    private _debugPrintMethod(text: string, verbosity: number, depth: number = 0): void {
        if (this._PRINT_CONSOLE_DEBUG_LEVEL < verbosity)
            return;

        let prefix = "";
        for (let i = 0; i < depth; ++i) {
            prefix += " ";
        }
        this._logger.log(prefix ? prefix + text : text);
    }

    public dispose(): void {
        if (this._disposed)
            return;

        this._disposed = true;
        this._scheduler.dispose();
        this.rightsEvaluating.removeAll();
        this.rightsEvaluated.removeAll();
    }

    /**
     * Z vrcholu zásobníku a prvku pod vrcholem zásobníku sestaví strukturu typu IRightsContexts
     * @param stackOfFramesContexts Zásobník kontextů
     */
    private static _buildRightsContextsObject(stackOfFramesContexts: TransparentStack<IRightsContext>): IRightsContexts {
        return { context: stackOfFramesContexts.peek(), previousContext: stackOfFramesContexts.peek(1) };
    }
}

/**
 * Průběžná data o celém procesu vyhodnocování práv
 */
export interface IGlobalEvaluationProcessDetails {
    frameRightsEvaluatorsMap: Map<ILogicalComponent, FrameRightsEvaluatorType>;

    evaluationCache: Map<string, Promise<IEvaluatedConditionResult>>;
}

/**
 * Průběžná data o procesu vyhodnocování práv ve specifické větvi stromu komponent
 */
export interface IEvaluationProcessDetails extends ITraverseProcessDetails {
    stackOfFramesContexts: TransparentStack<IRightsContext>;

    currentFrameRightsEvaluator: FrameRightsEvaluatorType;

    /** Příznak, zda je právě vyhodnocovaná větev stromu disablovaná nějakou nadřízenou komponentou. */
    disabledByParent?: boolean;
}

// TODO-rites: unit testy, vypnout
//this._logger.log("UnitTests");

const rightsUTs = () => {
    let currentTestName = "";

    interface ITestData {
        rightType: RightType;
        serverClassItemRights: IClassItemRights;
        formRightsToEvaluate: ItemRightsForRightLevels;
        itemRightsToEvaluate: ItemRightsForRightLevels;
        cachedConditions: Map<string, IEvaluatedConditionResult>;
        expectedResult: boolean;
        testName?: string;
    }

    const testData: ITestData[] = [];

    // Vytvoří data pro jeden běh testu. Např. pro soběh required
    const addTestData = (rightType: RightType, serverClassItemRights: IClassItemRights, formRightsToEvaluate: IItemRights[], itemRightsToEvaluate: IItemRights[], cachedConditions: { conditionID: string; value: boolean }[] | null, expectedResult: boolean, testName?: string) => {
        const cachedConditionsMap: Map<string, IEvaluatedConditionResult> = new Map<string, IEvaluatedConditionResult>();

        const formRightsForRightLevels: ItemRightsForRightLevels = new ItemRightsForRightLevels();

        for (const itemRight of formRightsToEvaluate) {
            formRightsForRightLevels.addItemRights(itemRight);
        }

        const itemRightsForRightLevels: ItemRightsForRightLevels = new ItemRightsForRightLevels();

        for (const itemRight of itemRightsToEvaluate) {
            itemRightsForRightLevels.addItemRights(itemRight);
        }

        if (cachedConditions) {
            for (const pair of cachedConditions) {
                cachedConditionsMap.set(pair.conditionID, { ID: pair.conditionID, Result: pair.value });
            }
        }

        if (!testName)
            testName = currentTestName + " (global)";

        testData.push({ rightType, serverClassItemRights, formRightsToEvaluate: formRightsForRightLevels, itemRightsToEvaluate: itemRightsForRightLevels, cachedConditions: cachedConditionsMap, expectedResult, testName });
    };

    // Vytvoří instanci ItemRights
    const createItemRight = (role: string, value: boolean, conditionID: string, isNegative: boolean, level?: number): IItemRights => {
        return {
            roleID: role,
            value: value,
            conditionID: conditionID,
            isNegative: isNegative,
            level: level || ((role && 3) || 4),
        };
    };

    // Vytvoří instanci IEvaluatedConditionResult - nacachovaná podmínka
    const cacheCond = (conditionID: string, value: boolean): { conditionID: string; value: boolean } => {
        return { conditionID, value };
    };

    // Spustí testy nad testovacími daty
    const runTestsOnData = async (testData: ITestData[]) => {
        let counter = 1;
        for (const oneTestData of testData) {
            await runTestOnData(oneTestData, counter);
            ++counter;
        }
    };

    // Spustí jeden test nad testovacími daty
    const runTestOnData = async (testData: ITestData, counter: number) => {
        const object: IRightsContext = null;
        const isStaticMethod: boolean = null;
        const ct = CancellationToken.none;
        const logger = console;

        const rightsManager = new RightsManager(null, null, null, () => null, logger as unknown as ISectionLogger);
        const result = await (rightsManager as any)["_evaluateRightTypeOnItemRights"].call(rightsManager, testData.rightType, testData.serverClassItemRights, testData.formRightsToEvaluate, testData.itemRightsToEvaluate, testData.cachedConditions, object, isStaticMethod, ct);
        logger.groupCollapsed(`Right unit test number ${counter} has ${result === testData.expectedResult ? "succeeded" : "FAILED"}.`); // `
        logger.log(`Result is "${result}". Expected was "${testData.expectedResult}". TestName = "${testData.testName}"`); // `
        logger.groupEnd();
    };

    //#region Inicializace-dat
    //addTestData(RightType., null, [createItemRight(null, null, null, false), createItemRight(null, null, null, false)], null, 0);
    currentTestName = "rolesConcurrReq,false vs. true cond. => false";
    addTestData(RightType.required, null, [], [createItemRight("11", null, "123", false), createItemRight("22", false, null, false)], [cacheCond("123", true)], false);

    currentTestName = "role,Req,false => false";
    addTestData(RightType.required, null, [], [createItemRight("22", false, null, false)], null, false);

    currentTestName = "role,Req,true => true";
    addTestData(RightType.required, null, [], [createItemRight("11", true, null, false)], null, true);

    currentTestName = "role,Req,serverTrue,roleLessFalse,RoleFalse => true";
    addTestData(RightType.required, { Name: "some", Type: ClassItemType.Attribute, Required: true }, [], [createItemRight(null, false, null, null), createItemRight("11", false, null, false)], null, true);

    currentTestName = "role,Req,serverFalse,roleLessTrue,roleFalse => true";
    addTestData(RightType.required, { Name: "some", Type: ClassItemType.Attribute, Required: false }, [], [createItemRight(null, true, null, null), createItemRight("11", false, null, false)], null, true);

    currentTestName = "role,Req,serverFalse,roleLessFalse,roleFalse => false";
    addTestData(RightType.required, { Name: "some", Type: ClassItemType.Attribute, Required: false }, [], [createItemRight(null, false, null, null), createItemRight("11", false, null, false)], null, false);

    /// <summary>
    /// Ověřuje souběh rolí na formulářové úrovni
    /// - pro roli A je Edit nastaven na Required=True
    /// - pro roli B je Edit nastaven na Required=False
    /// -> Edit není povinný
    /// </summary>
    currentTestName = "RightsFormElemenstRolesConcurrenceRequiredTrueVsRequiredFalse";
    addTestData(RightType.required, null, [], [createItemRight("11", true, null, false), createItemRight("22", false, null, false)], null, false);

    /// <summary>
    /// Ověřuje souběh rolí na formulářové úrovni
    /// - pro roli A je Edit nastaven na Required=False
    /// - pro roli B je Edit nastaven na Required=False
    /// -> Edit není povinný
    /// </summary>
    currentTestName = "RightsFormElemenstRolesConcurrenceRequiredFalseVsRequiredFalse";
    addTestData(RightType.required, null, [], [createItemRight("11", false, null, false), createItemRight("22", false, null, false)], null, false);

    /// <summary>
    /// Ověřuje souběh rolí na formulářové úrovni
    /// - pro roli A je na Editu nastavena podmínka na povinnost
    /// - pro roli B je Edit nastaven na Required=False
    /// -> Edit není nikdy povinný, nezávisle na podmínce
    /// </summary>
    currentTestName = "RightsFormElemenstRolesConcurrenceRequiredConditionVsRequiredFalse";
    addTestData(RightType.required, null, [], [createItemRight("11", null, "123", false), createItemRight("22", false, null, false)], [cacheCond("123", true)], false);
    addTestData(RightType.required, null, [], [createItemRight("11", null, "123", false), createItemRight("22", false, null, false)], [cacheCond("123", false)], false);

    /// <summary>
    /// Ověřuje souběh rolí na formulářové úrovni
    /// - pro roli A je na Editu nastavena podmínka na povinnost
    /// - pro roli B je na Editu nastavena podmínka na povinnost
    /// -> Edit není při splnění žádné z podmínek povinný
    /// </summary>
    currentTestName = "RightsFormElemenstRolesConcurrenceRequiredConditionVsRequiredCondition";
    addTestData(RightType.required, null, [], [createItemRight("11", null, "123", false), createItemRight("22", null, "456", false)], [cacheCond("123", false), cacheCond("456", true)], false);
    addTestData(RightType.required, null, [], [createItemRight("11", null, "123", false), createItemRight("22", null, "456", false)], [cacheCond("123", true), cacheCond("456", false)], false);

    /// <summary>
    /// Ověřuje souběh rolí na formulářové úrovni
    /// - bez role je na Editu nastaveno Required=false
    /// - pro roli A je na Editu nastaveno Required=true
    /// -> Edit je povinný
    /// </summary>
    currentTestName = "RightsFormElemenstRolesConcurrenceWithoutRoleRequiredFalseVsWithRoleRequiredTrue";
    addTestData(RightType.required, null, [], [createItemRight(null, false, null, false), createItemRight("22", true, null, false)], null, true);

    /// <summary>
    /// Ověřuje souběh rolí na formulářové úrovni
    /// - bez role je na Editu nastaveno Required=true
    /// - pro roli A je na Editu nastaveno Required=false
    /// -> Edit je povinný
    /// </summary>
    currentTestName = "RightsFormElemenstRolesConcurrenceWithoutRoleRequiredTrueVsWithRoleRequiredFalse";
    addTestData(RightType.required, null, [], [createItemRight(null, true, null, false), createItemRight("22", false, null, false)], null, true);

    /// <summary>
    /// Ověřuje souběh rolí na formulářové úrovni
    /// - bez role je na Editu nastavena podmínka na povinnost
    /// - pro roli A je na Editu nastavena podmínka na povinnost
    /// -> Edit je při splnění obou podmínek povinný
    /// </summary>
    currentTestName = "RightsFormElemenstRolesConcurrenceWithoutRoleRequiredConditionVsWithRoleRequiredCondition";
    addTestData(RightType.required, null, [], [createItemRight(null, null, "123", false), createItemRight("22", null, "456", false)], [cacheCond("123", false), cacheCond("456", true)], true);
    addTestData(RightType.required, null, [], [createItemRight(null, null, "123", false), createItemRight("22", null, "456", false)], [cacheCond("123", true), cacheCond("456", false)], true);

    currentTestName = "role,Delete,true";
    addTestData(RightType.delete, null, [], [createItemRight("11", true, null, false)], null, true);

    currentTestName = "role,Delete,false";
    addTestData(RightType.delete, null, [], [createItemRight("11", false, null, false)], null, false);

    //#endregion Inicializace-dat

    runTestsOnData(testData);
};
if (rightsUTs) {
    // rightsUTs();
}

//import '../../../Global/Base/Rights/CancelRunningRightsEvaluationSchedulerTests';
